import {
  BookOpen,
  Calculator,
  Clock,
  DollarSign,
  Gauge,
  Printer,
  Save,
  Share2,
  Sliders,
  Sparkles,
  ToggleLeft,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Separator } from "@/components/ui/separator"
import { cn } from "@/lib/utils"

type FactSheetGuideDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function Section({
  icon: Icon,
  title,
  children,
  className,
}: {
  icon: React.ElementType
  title: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn("flex gap-3", className)}>
      <div className="bg-primary/10 text-primary flex size-8 shrink-0 items-center justify-center rounded-lg">
        <Icon className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <h4 className="text-sm font-semibold">{title}</h4>
        <div className="text-muted-foreground mt-1 text-sm leading-relaxed">{children}</div>
      </div>
    </div>
  )
}

function TenKBadge({ label, color, desc }: { label: string; color: string; desc: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-block size-2.5 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-sm">
        <strong>{label}</strong> &ndash; {desc}
      </span>
    </div>
  )
}

export function FactSheetGuideDialog({ open, onOpenChange }: FactSheetGuideDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="size-5" />
            Use Case Fact Sheet Guide
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-5">
            <Section icon={BookOpen} title="What is this tool?">
              A one-page summary of your AI/automation opportunity for stakeholders, boards, and
              technical teams. Fill in the fields, run AI analysis, and share with your team.
            </Section>

            <Separator />

            <Section icon={Clock} title="Hours Saved per Person">
              Enter hours and choose <strong>per week</strong> (&times; 48 working weeks),{" "}
              <strong>per month</strong> (&times; 12), or <strong>one-time</strong>. All values
              convert to an annual figure automatically.
            </Section>

            <Section icon={DollarSign} title="Operating Cost">
              Enter cost and choose <strong>monthly</strong> (&times; 12) or <strong>yearly</strong>
              . Both feed into the annual ROI calculation.
            </Section>

            <Separator />

            <Section icon={Calculator} title="The $10K Rule">
              <div className="mt-1 space-y-1.5">
                <TenKBadge
                  color="hsl(var(--muted-foreground))"
                  desc="marginal benefit"
                  label="$10K/year = Minimal"
                />
                <TenKBadge
                  color="hsl(var(--success-ink))"
                  desc="solid business case"
                  label="$10K/quarter = Realistic"
                />
                <TenKBadge
                  color="hsl(var(--warning-ink))"
                  desc="high value, needs commitment"
                  label="$10K/month = Ambitious"
                />
                <TenKBadge
                  color="hsl(var(--destructive-ink))"
                  desc="extraordinary claims"
                  label="$10K/week = Low success rate"
                />
              </div>
            </Section>

            <Separator />

            <Section icon={Sliders} title="Feasibility Slider">
              Drag between three levels:{" "}
              <span className="text-destructive font-medium">Low/Complex</span> &rarr;{" "}
              <span className="text-warning font-medium">Medium</span> &rarr;{" "}
              <span className="text-success font-medium">High/Easy</span>.
            </Section>

            <Section icon={ToggleLeft} title="Toggle Fields">
              Click <strong>Time Savings</strong>, <strong>Quality Improvement</strong>,{" "}
              <strong>+R&amp;D Capabilities</strong>, and <strong>Improved Way of Work</strong> to
              toggle Yes / No.
            </Section>

            <Section icon={Gauge} title="Status">
              Click the status badge in the sidebar to cycle through:{" "}
              <span className="text-success">Implemented</span> &rarr;{" "}
              <span className="text-warning-ink">In Progress</span> &rarr;{" "}
              <span className="text-muted-foreground">Planned</span> &rarr;{" "}
              <span className="text-destructive">On Hold</span>.
            </Section>

            <Separator />

            <Section icon={Sparkles} title="AI Features">
              <ul className="mt-1 list-inside list-disc space-y-1">
                <li>
                  <strong>AI Draft Assist</strong> &ndash; Click the sparkle icon on any text field
                  to generate content suggestions.
                </li>
                <li>
                  <strong>QA Review</strong> &ndash; Analyzes completeness, consistency, and
                  quality. Gives a score, issues list, and improvement suggestions.
                </li>
                <li>
                  <strong>Sales Pitch</strong> &ndash; Generates a persuasive pitch based on your
                  problem, solution, and ROI data.
                </li>
                <li>
                  <strong>Claude Prompt</strong> &ndash; Creates a ready-to-use prompt for building
                  the described AI solution.
                </li>
              </ul>
            </Section>

            <Separator />

            <Section icon={Printer} title="Print / PDF">
              The action bar and parameters panel are hidden when printing. Use your browser's "Save
              as PDF" option for a clean export.
            </Section>

            <Section icon={Save} title="Saving &amp; Sharing">
              <ul className="mt-1 list-inside list-disc space-y-1">
                <li>
                  <strong>Save</strong> &ndash; Saves your use case to the cloud. All changes are
                  stored securely and accessible from any device.
                </li>
                <li>
                  <strong>Visibility</strong> &ndash; New use cases are private by default. Use the{" "}
                  <strong>Share</strong> dialog to make them visible to everyone or invite specific
                  people.
                </li>
                <li>
                  <strong>Import / Export .txt</strong> &ndash; Import a previously saved fact sheet
                  or export the current one as a text file for backup or sharing.
                </li>
              </ul>
            </Section>

            <Section icon={Share2} title="Sharing &amp; Collaboration">
              Use the <strong>Share</strong> button to invite colleagues as <strong>readers</strong>{" "}
              (view only) or <strong>editors</strong> (can modify). Search by name or group in your
              organization's directory.
            </Section>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button size="sm" onClick={() => onOpenChange(false)}>
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
